const { cloneShallow } = require('@/functions/clone');

/**
 * On the today view there is a filter that lets you filter events by category.
 * Events don't have a category. So what this actually means is "show me events that have tickets for them in this
 * category".
 * The purpose of this class is to determine which events apply for a category.
 */
class EventsByCategoryService {
    /**
     * @param {SessionEventRepository} sessionEventRepository
     * @param {TicketRepository} ticketRepository
     */
    constructor(
        sessionEventRepository,
        ticketRepository
    ) {
        this.sessionEventRepository = sessionEventRepository;
        this.ticketRepository = ticketRepository;

        this.eventIDsByCategoryCache = {};
    }

    /**
     * @param {number} categoryID
     *
     * @return {Promise<number[]>}
     */
    async getEventIDsByCategory(categoryID) {
        // Check for a cached result.
        if (this.eventIDsByCategoryCache.hasOwnProperty(categoryID)) {
            // Clone it so that we don't return the exact same array that's in the cache in case it's modified later.
            return cloneShallow(this.eventIDsByCategoryCache[categoryID]);
        }

        // First load all the tickets in this category.
        const tickets = await this.ticketRepository.findByCategory(categoryID);

        const eventIDs = [];
        tickets.forEach((ticket) => {
            if (ticket.event) {
                eventIDs.push(ticket.event.ID);
            }
        });

        // Store the results in the cache.
        // Clone it so that we don't return the exact same array that's in the cache in case it's modified later.
        this.eventIDsByCategoryCache[categoryID] = cloneShallow(eventIDs);

        return eventIDs;
    }

    /**
     * @param {number[]} categoryIDs
     *
     * @return {Promise<number[]>}
     */
    async getEventIDsByCategoryIDs(categoryIDs) {
        const allEventIDsArrays = await Promise.all(
            categoryIDs.map((id) => this.getEventIDsByCategory(id))
        );

        const combinedEventIDs = [...new Set(allEventIDsArrays.flat())];

        return combinedEventIDs;
    }
}

module.exports = EventsByCategoryService;
